/**
 * Lucide icons for NAV_GRAPH deep links (Cmd-K presentation).
 * Kept separate so nav-graph data stays free of icon coupling.
 */
import type { LucideIcon } from 'lucide-react';
import { COMPLIANCE_NAV_ICON_BY_HREF } from './nav-v2';
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  Award,
  Bandage,
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  BriefcaseBusiness,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Dumbbell,
  FileBarChart,
  FileCheck,
  FileSpreadsheet,
  FileStack,
  FileText,
  GraduationCap,
  Grid3x3,
  HeartPulse,
  Landmark,
  Layers,
  ListTree,
  Lock,
  Megaphone,
  MessageSquare,
  MessageSquareWarning,
  MonitorDot,
  OctagonAlert,
  PiggyBank,
  Pill,
  Route,
  Scale3d,
  Scroll,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShieldHalf,
  Stethoscope,
  Tag,
  TrendingUp,
  Upload,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
  Video,
  Workflow,
} from 'lucide-react';

/** Href → icon for every NAV_GRAPH_DEEP_LINKS entry. */
export const NAV_GRAPH_DEEP_LINK_ICONS: Readonly<Record<string, LucideIcon>> = {
  ...COMPLIANCE_NAV_ICON_BY_HREF,
  '/people/workers?view=recruitment': UserPlus,
  '/people/development?view=flexible-work': CalendarClock,
  '/people/policies-conduct/cases?view=grievances': MessageSquareWarning,
  '/people/policies-conduct/cases?view=return-to-work': Bandage,
  '/people/policies-conduct/cases?view=approvals': ClipboardCheck,
  '/compliance/workforce/import': Upload,
  '/people/workers': Users,
  '/people/onboarding': UserPlus,
  '/people/compliance': ClipboardCheck,
  '/people/learning': GraduationCap,
  '/people/policies-conduct': FileText,
  '/people/policies-conduct/governance': FileCheck,
  '/settings/people': Settings,
  '/settings/comfort-coordination': HeartPulse,
  '/contractors': BriefcaseBusiness,
  '/me/onboarding': ClipboardList,
  '/people/development': TrendingUp,
  '/people/development/performance': TrendingUp,
  '/people/compliance/obligations': ClipboardList,
  '/people/development/supervision': UserCog,
  '/people/evidence': FileCheck,
  '/people/workers/new': UserPlus,
  '/people/workers/bulk-invite': Upload,
  '/people/learning/courses': GraduationCap,
  '/people/learning/analytics': BarChart3,
  '/people/compliance/screening': ShieldAlert,
  '/people/compliance/certifications': FileCheck,
  '/people/offboarding': UserCog,
  '/people/development/casual-conversion': ArrowLeftRight,
  '/settings/people/assignment-requirements': Settings,
  '/compliance/whs': ShieldAlert,
  '/compliance/ipc': ShieldHalf,
  '/compliance/data-breach': Lock,
  '/compliance/whistleblower': Megaphone,
  '/compliance/audit-trail': Scroll,
  '/compliance/rn-coverage': HeartPulse,
  '/compliance/record-recovery': ShieldAlert,
  '/compliance/governance/skills-matrix': Grid3x3,
  '/compliance/governance/conflicts': Scale3d,
  '/compliance/governance/qcab': UsersRound,
  '/compliance/evidence-packs': FileStack,
  '/telehealth': Video,
  '/monitoring': MonitorDot,
  '/care-plans/bulk-replan': ClipboardCheck,
  '/assessments/library': BookOpen,
  '/medications': Pill,
  '/medications/monitoring': Pill,
  '/medications/monitoring/setup': Pill,
  '/finance/respite-billing': Calendar,
  '/finance/billing': FileText,
  '/finance/billing/readiness': ClipboardCheck,
  '/finance/payers': Users,
  '/finance/charges': Tag,
  '/finance/statements': FileSpreadsheet,
  '/pricing/rate-cards': Tag,
  '/head-office': Landmark,
  '/dashboards/huddle': MessageSquare,
  '/dashboards/home-care-ops': HeartPulse,
  '/dashboards/facility-ops': Building2,
  '/me/shift-offers': Bell,
  '/me/shift-swaps': ArrowLeftRight,
  '/me/timesheets': FileSpreadsheet,
  '/me/messages': MessageSquare,
  '/me/pay-documents': FileText,
  '/me/leave': Briefcase,
  '/me/casual-conversion': ArrowLeftRight,
  '/me/hours': Clock,
  '/me/preferences': Settings,
  '/me/contractor': BriefcaseBusiness,
  '/dashboards/workforce-analytics': Users,
  '/dashboards/region-roll-up': BarChart3,
  '/care-pathways': Route,
  '/clinical/medication-incidents': OctagonAlert,
  '/clinical/caseload': Stethoscope,
  '/clinical/delegations': UserCheck,
  '/clinical/pathways': ListTree,
  '/clinical/restorative': Dumbbell,
  '/clinical/overdue-assessments': CalendarClock,
  '/medications/discrepancies': AlertTriangle,
  '/programs': Layers,
  '/waitlist': Clock,
  '/finance/accommodation': Building2,
  '/residential/care-minutes/an-acc': ClipboardList,
  '/residential/dap-accrual': CalendarClock,
  '/compliance/prudential/deposits': PiggyBank,
  '/pricing': Tag,
  '/analytics/facilities': Building2,
  '/analytics/benchmarking': BarChart3,
  '/care-plans': ClipboardList,
  '/assessments': FileCheck,
  '/people': UsersRound,
  '/home-care/readiness': ClipboardCheck,
  '/home-care/budgets': PiggyBank,
  '/home-care/funding-changes': PiggyBank,
  '/cm-tasks': Clock,
  '/compliance': ShieldCheck,
  '/compliance/complaints': MessageSquareWarning,
  '/compliance/restrictive-practices': OctagonAlert,
  '/compliance/continuous-improvement': TrendingUp,
  '/compliance/documents': FileStack,
  '/compliance/quality-indicators': Award,
  '/compliance/audit-program': ClipboardCheck,
  '/me/roster': Calendar,
  '/me/work-readiness': ClipboardCheck,
  '/dashboards/caseload': Users,
  '/compliance/calendar?view=agenda': Clock,
  '/observations': Activity,
  '/wounds': Bandage,
  '/medications/mar': ClipboardCheck,
  '/clinical/alerts': Bell,
  '/clinical/escalations': AlertTriangle,
  '/clinical/handovers': ArrowLeftRight,
  '/workforce/availability': CalendarCheck,
  '/availability': CalendarClock,
  '/me/availability': CalendarClock,
  '/residential': Building2,
  '/residential/occupancy': Building2,
  '/workflows/instances': Workflow,
  '/activity': Activity,
  '/home-care/service-delivery': HeartPulse,
  '/analytics': BarChart3,
  '/reports': FileBarChart,
  '/finance/budgets/carry-over': PiggyBank,
  '/compliance/governance/risk-register': ListTree,
  '/reports/subscriptions': CalendarClock,
  '/payroll': FileSpreadsheet,
  '/compliance/responsible-persons': UserCheck,
  '/compliance/registration': BookOpen,
  '/compliance/prudential': Landmark,
  '/compliance/prudential/reports': FileBarChart,
  '/compliance/prudential/accommodation-agreements': FileCheck,
  '/roster/dispatch': Calendar,
  '/roster/time-review': Clock,
  '/roster/timesheets': FileSpreadsheet,
  '/roster/timesheets/reconciliation': ClipboardCheck,
  '/workforce/timesheets/disputes': MessageSquareWarning,
};

export function iconForNavGraphDeepLink(href: string, fallback: LucideIcon): LucideIcon {
  return NAV_GRAPH_DEEP_LINK_ICONS[href] ?? fallback;
}
